import React, { Component } from 'react'
import { Nav, NavItem, NavLink as RsNavLink } from 'reactstrap'
import classNames from 'classnames'
import nav from './_nav'
import { NavLink } from '../../../router'
import { checkPermission } from '../../../utils/helpers'
import Rephrase from '../../../utils/Rephrase'
import { withLDConsumer } from 'launchdarkly-react-client-sdk'
import { Icon } from '@ant-design/compatible'
import ReggoraLogo from '../ReggoraLogo/Logo'
import apiClient from '@app/apiClient'
import { fetchNeedsActionData } from '@app/pages/needsAction/needsActionApi'
import { onNeedsActionChanged } from '@app/pages/needsAction/needsActionEvents'

class Sidebar extends Component {
  static defaultProps = {
    isLogoVisible: false,
  }

  state = {
    needsActionCount: null,
    // Whether this user's role has any Needs Action views assigned. Starts
    // false so the nav item is hidden until we know it belongs there -- the
    // opposite default would flash an item that then disappears for the many
    // roles with nothing assigned.
    hasNeedsActionViews: false,
  }

  // Not gated on the needsActionViews flag. LaunchDarkly mutates the flags
  // object in place rather than replacing it, so its value can flip from
  // false to true without React re-rendering us -- a flag check here would
  // read the pre-identify value and never run again. The flag is applied in
  // render() instead, which reads it live. The request is cheap and returns
  // an empty list for roles with nothing assigned.
  componentDidMount() {
    this.loadNeedsAction()
    // Resolving or withdrawing a request changes the count, and this component
    // otherwise fetches once and never again -- leaving a stale number on the
    // one screen where it is most obviously wrong.
    this._unsubscribeNeedsAction = onNeedsActionChanged(this.loadNeedsAction)
  }

  // The Needs Action nav item only exists for roles an admin has assigned
  // views to, so its visibility and its badge come from the same fetch.
  // Never lets a failure here affect the rest of the sidebar.
  loadNeedsAction = async () => {
    try {
      const api = apiClient(API2_URL)
      const { views, count } = await fetchNeedsActionData(api, { limit: 1 })
      if (this._isUnmounted) {
        return
      }
      this.setState({ needsActionCount: count, hasNeedsActionViews: views.length > 0 })
    } catch (err) {
      // Swallow: leaves the item hidden, which is the safe default.
    }
  }

  componentWillUnmount() {
    this._isUnmounted = true
    if (this._unsubscribeNeedsAction) {
      this._unsubscribeNeedsAction()
    }
    // Cleanup any subscriptions
    if (this.props.flags) {
      this.props.flags.off && this.props.flags.off()
    }
  }

  handleClick = (e) => {
    e.preventDefault()
    e.target.parentElement.classList.toggle('open')
  }

  getMinimize = (e) => {
    e.preventDefault()
    this.props.toggleMinimizeSidebar()
  }

  activeRoute = (routeName, props) => {
    // return this.props.location.pathname.indexOf(routeName) > -1 ? 'nav-item nav-dropdown open' : 'nav-item nav-dropdown';
    return props.location.pathname.indexOf(routeName) > -1 ? 'nav-item nav-dropdown open' : 'nav-item nav-dropdown'
  }

  hideMobile = () => {
    if (document.body.classList.contains('sidebar-mobile-show')) {
      document.body.classList.toggle('sidebar-mobile-show')
    }
  }

  render() {
    const props = this.props
    const showReviewSettingsMenu = Boolean(props.flags?.showReviewSettingsMenu)
    const pylonSupportSso = Boolean(props.flags?.pylonSupportSso)

    // simple wrapper for nav-title item
    const wrapper = (item) => {
      return item.wrapper && item.wrapper.element
        ? React.createElement(item.wrapper.element, item.wrapper.attributes, item.name)
        : item.name
    }

    // nav list section title
    const title = (title, key) => {
      const classes = classNames('nav-title', title.class)
      return (
        <li key={key} className={classes}>
          <Rephrase>{wrapper(title)}</Rephrase>
        </li>
      )
    }

    // nav list divider
    const divider = (divider, key) => {
      const classes = classNames('divider', divider.class)
      return <li key={key} className={classes}></li>
    }

    // nav label with nav link
    const navLabel = (item, key) => {
      const classes = {
        item: classNames('hidden-cn', item.class),
        link: classNames('nav-label', item.class ? item.class : ''),
        icon: classNames(
          !item.icon ? 'fa fa-circle' : item.icon,
          item.label.variant ? `text-${item.label.variant}` : '',
          item.label.class ? item.label.class : '',
        ),
      }
      return navLink(item, key, classes)
    }

    // nav item with nav link
    const navItem = (item, key) => {
      const classes = {
        item: classNames(item.class),
        link: classNames('nav-link', item.variant ? `nav-link-${item.variant}` : ''),
        icon: classNames(item.icon),
      }
      return navLink(item, key, classes)
    }
    // nav link
    const navLink = (item, key, classes) => {
      const url = item.url ? item.url : ''
      // Only the Needs Action item carries a live count today. Render it as a red
      // overlay pinned to the icon so it's visible even when the sidebar is
      // collapsed (icon-only) — not just in the hover flyout. A plain span (not the
      // CoreUI `.badge`) avoids CoreUI's rule that hides badges when minimized.
      const badgeCount = item.url === 'needsAction' ? this.state.needsActionCount : null
      const badge =
        badgeCount != null && badgeCount > 0 ? (
          <span
            data-testid="needs-action-nav-badge"
            style={{
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              right: 8,
              minWidth: 18,
              height: 18,
              padding: '0 5px',
              borderRadius: 9,
              background: '#f5222d',
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              lineHeight: '18px',
              textAlign: 'center',
              zIndex: 2,
              pointerEvents: 'none',
            }}
          >
            {badgeCount}
          </span>
        ) : null
      const icon =
        item.name === 'Review Settings' ? (
          <i>
            <Icon type={classes.icon} />
          </i>
        ) : (
          <i className={classes.icon} />
        )
      if (item.children) {
        return (
          <li
            key={key}
            className={`nav-item nav-dropdown ${props.dropdown[key] ? 'active open' : ''}`}
            onClick={() => props.toggleDropdown(key)}
          >
            <a key={key} className="nav-link nav-dropdown-toggle">
              {icon}
              <Rephrase>{item.name}</Rephrase>
            </a>
            <ul className="nav-dropdown-items">
              {item.children
                .filter(({ url, permission, publicRoute }) =>
                  publicRoute || checkPermission(permission || `${url}_route`)
                )
                .map((child, idx) => (
                  <NavLink
                    key={`${key}-${idx}`}
                    to={child.url}
                    className={`${classes.link} nav-link-child`}
                    activeClassName="active"
                    onClick={this.hideMobile}
                  >
                    <Rephrase>{child.name}</Rephrase>
                  </NavLink>
                ))}
            </ul>
          </li>
        )
      }
      return (
        <NavItem key={key} className={classes.item} style={{ position: 'relative' }}>
          {isExternal(url) ? (
            <RsNavLink target={item.target} href={url} className={classes.link}>
              {icon}
              {item.name}
            </RsNavLink>
          ) : (
            <NavLink to={url} className={classes.link} activeClassName="active" onClick={this.hideMobile}>
              {icon}
              {item.name}
            </NavLink>
          )}
          {badge}
        </NavItem>
      )
    }
    const navType = (item, idx) =>
      item.title
        ? title(item, idx)
        : item.divider
          ? divider(item, idx)
          : item.label
            ? navLabel(item, idx)
            : navItem(item, idx)

    const getNavLink = (item, index) => {
      if (item.demo) {
        if (props.user.data && props.user.data.is_demo) {
          return navType(item, index)
        } else {
          return null
        }
      } else {
        return navType(item, index)
      }
    }

    const navList = (routes) => {
      return routes
        .filter(Boolean)
        .filter((route) => route.url)
        .filter(({ permission, publicRoute, url }) =>
          publicRoute || checkPermission(permission || `${url}_route`)
        )
        // Needs Action is driven entirely by the views an admin assigned to
        // this role; with none assigned the page would always be empty, so the
        // item is dropped rather than shown leading nowhere.
        // Flag read here, not at fetch time: render re-reads it live, so the
        // item appears as soon as LaunchDarkly settles.
        .filter(
          (route) =>
            route.url !== 'needsAction' ||
            (Boolean(props.flags?.needsActionViews) && this.state.hasNeedsActionViews),
        )
        .map((item, index) => getNavLink(item, index))
    }

    const isExternal = (url) => {
      const link = url ? url.substring(0, 4) : ''
      return link === 'http'
    }

    return (
      <div className="sidebar">
        {props.isLogoVisible && <ReggoraLogo containerClassName={props.minimize ? 'logo-minimized' : 'logo-expanded'} />}
        <nav className="sidebar-nav">
          <Nav>{navList(nav(showReviewSettingsMenu, pylonSupportSso).items, withLDConsumer)}</Nav>
        </nav>
        <button className="sidebar-minimizer" type="button" onClick={this.getMinimize} />
      </div>
    )
  }
}

const SidebarWithLD = withLDConsumer()(Sidebar)

export default SidebarWithLD
